// IMPORTANT: Sentry must be initialized before any graph-db wiring below
// (create_graphdb_client / init_ws / init_http). Module import order is guaranteed.
import '$lib/tools/sentry';
import { report_error } from '$lib/tools/sentry';
import { CapacitorHttp } from '@capacitor/core';
import { auth_sdk } from './graph-db/auth_sdk';
import { create_graphdb_client } from './graph-db';
import { init_db } from './graph-db/localdb/db';
import { graphql } from './graph-db/transport/graphql';
import { init_http, type HttpFetchInput } from './graph-db/transport/http';
import { init_ws, close_ws, ws_has_client } from './graph-db/transport/ws';

// ─── Auto-start (module scope) ─────────────────────────────────────────────
// Local IndexedDB wiring — per-app database name + schema. Each app on the
// platform names its own DB ('driver', 'seller', ...); keep this schema in
// sync with whatever tables the app's stores create.
init_db({
	name: 'driver',
	stores: {
		routes: 'id, date_created, date_updated, is_active, assigned_driver_id',
		shipments: 'id, date_created, date_updated, route_id',
		chat_state: 'id, date_created, date_updated, shipment_id',
		driver: 'id, date_created, date_updated',
		payout_invoices: 'id, date_created, date_updated',
		collection_invoices: 'id, date_created, date_updated',
		payments: 'id, date_created, date_updated',
		meta: 'key'
	}
});

// ─── GraphDB client (legos client pattern) ────────────────────────────────
// This module is THE single entry point of the graph-db library. Importing
// ANY value from it (auth_state, stores, components) starts the important
// parts — the auth SDK (endpoint, requested role, Capacitor fetch fn) and
// the HTTP/WS transport — because everything else relies on them being
// ready. Do not import the deep modules (graph-db/auth_sdk, transport/*)
// directly; go through this entry so the wiring always runs.
export const graphdb = create_graphdb_client({
	storage: 'capacitor',
	requested_role: 'driver',
	auth_endpoint: import.meta.env.VITE_AUTH_ENDPOINT,
	graph_endpoint: import.meta.env.VITE_GRAPHQL_ENDPOINT,
	graph_ws_endpoint: import.meta.env.VITE_GRAPHQL_WS_ENDPOINT,
	fetch_function: async ({ url, access_token, body, clean_object }) => {
		const fetch_options = clean_object({
			url,
			headers: {
				'Content-Type': 'application/json',
				Authorization: access_token ? `Bearer ${access_token}` : undefined
			},
			data: body ? JSON.stringify(body) : undefined
		});
		const response = await CapacitorHttp.post(fetch_options);
		return response.data ?? null;
	}
});

const new_fetch_fn = async (input: HttpFetchInput): Promise<{ data?: unknown; errors?: unknown[] } | null> => {
	try {
		const headers: Record<string, string> = { 'Content-Type': 'application/json' };
		if (input.access_token) headers.Authorization = `Bearer ${input.access_token}`;
		const response = await CapacitorHttp.post({
			url: input.url,
			headers,
			data: input.body ? JSON.stringify(input.body) : undefined
		});
		return (response.data ?? null) as { data?: unknown; errors?: unknown[] } | null;
	} catch (error) {
		// Stop silently swallowing HTTP failures — surface them to Sentry so
		// fetch-side outages are visible on the board.
		report_error(error, {
			message: 'http.fetch_error',
			tags: { component: 'http', phase: 'fetch' },
			extras: { url: input.url, has_token: Boolean(input.access_token) }
		});
		return null;
	}
};

init_http({
	endpoint: import.meta.env.VITE_GRAPHQL_ENDPOINT,
	token_provider: () => auth_sdk.get_access_token(),
	fetch_fn: new_fetch_fn
});

init_ws({
	endpoint: import.meta.env.VITE_GRAPHQL_WS_ENDPOINT ?? '',
	token_provider: () => auth_sdk.get_access_token()
});

// ─── Public API ────────────────────────────────────────────────────────────
export { auth_sdk, graphql, close_ws, ws_has_client };
export * from './graph-db/generated/schema';
